import { VehicleSection } from '@/components/VehicleSection/VehicleSection';
import { HowItWorksCTA } from '@/reports/shared/ReportSection/components';
import { AutomobileWarranties } from '@/reports/types/vehicles';
import { Text, Table, useTheme, Icons } from '@/theme/index';

export interface VehicleWarrantyProps {
  /** A list of all warranty data */
  vehicleWarrantyList: AutomobileWarranties;
}

function HowItWorksCTAContent() {
  return (
    <Text
      variant="body2"
      fontSize="0.9rem"
      color={(theme) => theme.palette.grey[800]}
    >
      Warranty information for vehicles can differ due to manufacturer-specific
      variations and data reporting differences. While we aim to provide
      comprehensive warranty details, some vehicles may not display warranty
      information. Factors such as manufacturer disclosure practices and limited
      historical warranty data for older or less common models may contribute to
      this.
    </Text>
  );
}

/** It renders a table view of warranty listings */
export const VehicleWarranty: React.FC<VehicleWarrantyProps> = ({
  vehicleWarrantyList,
}) => {
  const theme = useTheme();
  const hasStatusData =
    vehicleWarrantyList?.filter((warranty) => warranty.is_active !== undefined)
      .length > 0;
  const header = ['Warranties', 'Miles', 'Months'].map((innerHtml) => {
    return (
      <Text variant="h5" sx={{ p: 1, m: 0 }}>
        {innerHtml}
      </Text>
    );
  });

  if (hasStatusData) {
    header.push(
      <Text variant="h5" sx={{ p: 1, m: 0 }}>
        Status
      </Text>
    );
  }

  const modifiedWarrantyList = vehicleWarrantyList?.map((warranty) => {
    const name = warranty?.type || '-';
    const miles = warranty?.miles || '-';
    const months = warranty?.months || '-';
    const status = warranty?.is_active ? 'Likely Active' : 'Likely Expired';
    const StatusIcon = Icons[warranty?.is_active ? 'Check' : 'Warning'];
    const statusColor = warranty?.is_active ? 'success.main' : 'error.main';
    const output = [name, miles, months].map((innerHtml) => {
      return (
        <Text variant="body2" sx={{ p: theme.spacing(1) }}>
          {innerHtml}
        </Text>
      );
    });

    if (hasStatusData) {
      output.push(
        <Text variant="body2" sx={{ p: theme.spacing(1) }}>
          <StatusIcon
            fontSize="small"
            sx={{
              fontSize: '1.125rem',
              color: statusColor,
              verticalAlign: 'bottom',
            }}
          />
          {status}
        </Text>
      );
    }

    return output;
  });

  const modifiedMobileWarrantyList = vehicleWarrantyList?.map((warranty) => {
    const name = warranty?.type || '-';
    const miles = warranty?.miles || '-';
    const months = warranty?.months || '-';
    const status = warranty?.is_active ? 'Likely Active' : 'Likely Expired';

    return (
      <Table
        className="striped flipped mobile"
        headers={[
          <Text
            variant="h5"
            sx={{
              p: 0,
              m: 0,
            }}
          >
            {name}
          </Text>,
        ]}
        rows={[
          ['Miles', miles],
          ['Month', months],
          ['Status', status],
        ]}
      />
    );
  });

  return (
    <VehicleSection
      headers={header}
      tableData={modifiedWarrantyList}
      mobileTableData={modifiedMobileWarrantyList}
      reportSectionTitle="Possible Warranties"
      emptySectionTitle="possible warranties"
      tableTitle="Specified period of time for repairs or replacements"
      sectionId="vehicle-warranty"
      howItWorks={<HowItWorksCTA content={HowItWorksCTAContent()} />}
    />
  );
};
